'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Calendar, BedDouble, Users, ChevronDown as LucideChevronDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { CmsRoom } from '@/lib/cms';
import { fetchAvailableRoomTypes } from '@/lib/api/availability';
import { buildSignedBookingUrl } from '@/lib/booking-navigate';
import { ButtonSpinner } from '@/components/ButtonSpinner';
import { useRoomSelection } from '@/lib/room-selection-context';

const { colors, fonts } = siteTheme;
const navy = '#0D2F44';
const NAV_H_FALLBACK = 96; // px fallback until header is measured

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];

type GuestEntry = { adults: number; children: number };

/* ── Icon helpers ── */
const BedIcon    = () => <BedDouble size={13} color={navy} style={{ flexShrink: 0, opacity: 0.65 }} />;
const PeopleIcon = () => <Users     size={13} color={navy} style={{ flexShrink: 0, opacity: 0.65 }} />;
const CalendarIcon = () => <Calendar size={12} color={navy} style={{ flexShrink: 0, opacity: 0.65 }} />;
const ArrowLeft  = () => <ChevronLeft  size={11} color={navy} strokeWidth={1.5} />;
const ArrowRight = () => <ChevronRight size={11} color={navy} strokeWidth={1.5} />;
const ChevronDown = ({ open, color = 'rgba(13,47,68,0.5)' }: { open: boolean; color?: string }) => (
  <LucideChevronDown size={9} color={color} strokeWidth={1.3}
    style={{ pointerEvents: 'none', flexShrink: 0, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }}
  />
);

/* ── Helpers ── */
function useClickOutside(ref: React.RefObject<HTMLElement | null>, cb: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    function h(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) cb(); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, cb, enabled]);
}

function isoToLocal(iso: string) { return iso ? new Date(iso + 'T00:00:00') : null; }
function toISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function fmtShort(iso: string) {
  const d = isoToLocal(iso);
  return d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Select';
}

/* ── Dropdown panel base style ── */
const dropPanel: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 0.4rem)',
  zIndex: 300,
  background: 'rgba(250,249,247,0.98)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(13,47,68,0.13)',
  borderRadius: '0.8rem',
  boxShadow: '0 12px 40px rgba(0,0,0,0.16), 0 4px 12px rgba(0,0,0,0.08)',
  maxWidth: 'calc(100vw - 1.6rem)',
};

/* ── Full-height bar field (label + value + chevron) ── */
function BarField({ label, icon, value, open, onClick }: {
  label: string; icon: React.ReactNode; value: string; open: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', height: '100%',
        border: 'none', background: 'transparent', cursor: 'pointer',
        padding: '0 1.6rem',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(13,47,68,0.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        display: 'flex', alignItems: 'center', gap: '0.35rem',
        fontFamily: fonts.sans, fontSize: '0.76rem', fontWeight: 600,
        letterSpacing: '0.13em', textTransform: 'uppercase',
        color: 'rgba(13,47,68,0.52)', marginBottom: '0.22rem',
      }}>
        {icon} {label}
      </span>
      <span style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%',
        fontFamily: fonts.sans, fontSize: '1.2rem', fontWeight: 400, color: navy,
      }}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
        <ChevronDown open={open} />
      </span>
    </button>
  );
}

const VDivider = () => (
  <div style={{ width: '1px', background: 'rgba(13,47,68,0.13)', alignSelf: 'stretch', flexShrink: 0, margin: '0.75rem 0' }} />
);

/* ── Capacity helpers ── */
function maxCapacity(rooms: CmsRoom[]) {
  const maxA = rooms.length ? Math.max(...rooms.map(r => r.max_adults   ?? 2)) : 2;
  const maxC = rooms.length ? Math.max(...rooms.map(r => r.max_children ?? 1)) : 1;
  return { maxA: Math.max(1, maxA), maxC: Math.max(0, maxC) };
}
function neededRooms(adults: number, children: number, maxA: number, maxC: number): number {
  const fromA = Math.ceil(adults / maxA);
  const fromC = maxC > 0 && children > 0 ? Math.ceil(children / maxC) : 0;
  return Math.max(1, fromA, fromC);
}

/* ── Counter row ── */
function CounterRow({ label, sub, value, onChange, min = 0, max = 10 }: {
  label: string; sub?: string; value: number;
  onChange: (v: number) => void; min?: number; max?: number;
}) {
  const btn = (dis: boolean): React.CSSProperties => ({
    width: '2.6rem', height: '2.6rem', borderRadius: '50%',
    border: `1px solid rgba(13,47,68,${dis ? '0.1' : '0.22'})`,
    background: 'transparent', color: navy, fontSize: '1.7rem', lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.28 : 1, fontFamily: fonts.sans, fontWeight: 300,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 0', gap: '1rem' }}>
      <div>
        <p style={{ fontFamily: fonts.sans, fontSize: '1.25rem', fontWeight: 500, color: navy }}>{label}</p>
        {sub && <p style={{ fontFamily: fonts.sans, fontSize: '1rem', color: 'rgba(13,47,68,0.45)', marginTop: '0.1rem' }}>{sub}</p>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexShrink: 0 }}>
        <button type="button" onClick={() => onChange(Math.max(min, value-1))} style={btn(value<=min)}>−</button>
        <span style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 600, color: navy, minWidth: '1.4rem', textAlign: 'center' }}>{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value+1))} style={btn(value>=max)}>+</button>
      </div>
    </div>
  );
}
const hr = <div style={{ height: '1px', background: 'rgba(13,47,68,0.07)' }} />;
const DoneBtn = ({ onClick }: { onClick: () => void }) => (
  <button type="button" onClick={onClick} className="btn-primary"
    style={{ marginTop: '1rem', width: '100%' }}>Done</button>
);

/* ── Rooms dropdown — no anchorRef, click-outside handled by parent widget ── */
function RoomsDropdown({ roomCounts, onCount, roomTypes, open, setOpen, alignRight = false }: {
  roomCounts: Record<string,number>; onCount: (s:string,n:number)=>void;
  roomTypes: CmsRoom[]; open: boolean; setOpen: (v:boolean)=>void; alignRight?: boolean;
}) {
  if (!open) return null;
  const hasTypes = roomTypes.length > 0;
  return (
    <div style={{ ...dropPanel, left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto', minWidth: '28rem', padding: '0.2rem 1.4rem 1.2rem' }}>
      {hasTypes ? roomTypes.map((rt, i) => (
        <div key={rt.slug}>
          {i > 0 && hr}
          <CounterRow label={rt.name} value={roomCounts[rt.slug]??0} onChange={n=>onCount(rt.slug,n)} max={5} />
        </div>
      )) : (
        <CounterRow label="Rooms" value={roomCounts['__rooms']??1} onChange={n=>onCount('__rooms',n)} min={1} max={10} />
      )}
      <DoneBtn onClick={() => setOpen(false)} />
    </div>
  );
}

/* ── Guests dropdown — no anchorRef, click-outside handled by parent widget ── */
function GuestsDropdown({ selectedRooms, guestPerRoom, onGuestChange, simpleAdults, simpleChildren, onSimpleAdults, onSimpleChildren, hasTypes, open, setOpen, alignRight = false, simpleRoomCount, simpleMaxAdultsPerRoom, simpleMaxChildrenPerRoom }: {
  selectedRooms: CmsRoom[]; guestPerRoom: Record<string,GuestEntry>;
  onGuestChange: (slug:string, field:keyof GuestEntry, v:number)=>void;
  simpleAdults: number; simpleChildren: number;
  onSimpleAdults: (v:number)=>void; onSimpleChildren: (v:number)=>void;
  hasTypes: boolean; open: boolean; setOpen: (v:boolean)=>void; alignRight?: boolean;
  simpleRoomCount: number; simpleMaxAdultsPerRoom: number; simpleMaxChildrenPerRoom: number;
}) {
  if (!open) return null;
  const simpleCapA = simpleRoomCount * simpleMaxAdultsPerRoom;
  const simpleCapC = simpleRoomCount * simpleMaxChildrenPerRoom;
  return (
    <div style={{ ...dropPanel, left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto', minWidth: '28rem', padding: '0.4rem 1.4rem 1.2rem' }}>
      {hasTypes && selectedRooms.length === 0 && (
        <p style={{ fontFamily: fonts.sans, fontSize: '1.2rem', color: 'rgba(13,47,68,0.45)', textAlign: 'center', padding: '1rem 0' }}>
          Please select a room first
        </p>
      )}
      {hasTypes && selectedRooms.map((rt, i) => {
        const e    = guestPerRoom[rt.slug] ?? { adults: 1, children: 0 };
        const maxA = rt.max_adults   ?? 2;
        const maxC = rt.max_children ?? 1;
        return (
          <div key={rt.slug}>
            {i > 0 && <div style={{ height: '1px', background: 'rgba(13,47,68,0.1)', margin: '0.2rem 0' }} />}
            <p style={{ fontFamily: fonts.serif, fontWeight: 300, fontSize: '1.3rem', color: colors.gold, paddingTop: '0.7rem', paddingBottom: '0.1rem' }}>{rt.name}</p>
            <CounterRow label="Adults per room"   value={e.adults}   onChange={v=>onGuestChange(rt.slug,'adults',v)}   min={1} max={maxA} />
            <CounterRow label="Children per room" sub="0 – 17 Years Old" value={e.children} onChange={v=>onGuestChange(rt.slug,'children',v)} max={maxC} />
            <p style={{ fontFamily: fonts.sans, fontSize: '1.05rem', color: 'rgba(13,47,68,0.4)', paddingBottom: '0.35rem' }}>
              Max {maxA} adult{maxA!==1?'s':''} · {maxC} child{maxC!==1?'ren':''} for this room type
            </p>
          </div>
        );
      })}
      {!hasTypes && (
        <>
          <CounterRow label="Adults"   value={simpleAdults}   onChange={onSimpleAdults}   min={1} max={simpleCapA} />
          {hr}
          <CounterRow label="Children" sub="0 – 17 Years Old" value={simpleChildren} onChange={onSimpleChildren} max={simpleCapC} />
          <p style={{ fontFamily: fonts.sans, fontSize: '1.1rem', color: 'rgba(13,47,68,0.42)', textAlign: 'center', padding: '0.5rem 0 0.2rem' }}>
            Max {simpleMaxAdultsPerRoom} adult{simpleMaxAdultsPerRoom!==1?'s':''} · {simpleMaxChildrenPerRoom} child{simpleMaxChildrenPerRoom!==1?'ren':''} per room
          </p>
        </>
      )}
      <DoneBtn onClick={() => setOpen(false)} />
    </div>
  );
}

/* ── Date dropdown — no anchorRef, click-outside handled by parent widget ── */
function DateDropdown({ value, onChange, min, open, setOpen, alignRight=false }: {
  value: string; onChange: (iso:string)=>void; min?: string;
  open: boolean; setOpen: (v:boolean)=>void; alignRight?: boolean;
}) {
  const selectedDate = isoToLocal(value);
  const todayDate    = new Date(); todayDate.setHours(0,0,0,0);
  const minDate      = min ? isoToLocal(min) : null;
  const [viewYear,  setViewYear]  = useState(selectedDate?.getFullYear() ?? todayDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate?.getMonth()    ?? todayDate.getMonth());
  useEffect(() => { const d=isoToLocal(value); if(d){ setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); } }, [value]);

  const firstDay    = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const startOff    = (firstDay.getDay()+6)%7;
  const cells: (number|null)[] = [...Array(startOff).fill(null), ...Array.from({length:daysInMonth},(_,i)=>i+1)];
  while (cells.length%7) cells.push(null);

  const isDis = (d:number) => !!(minDate && new Date(viewYear,viewMonth,d) < minDate);
  const isSel = (d:number) => !!(selectedDate && selectedDate.getFullYear()===viewYear && selectedDate.getMonth()===viewMonth && selectedDate.getDate()===d);
  const isTod = (d:number) => todayDate.getFullYear()===viewYear && todayDate.getMonth()===viewMonth && todayDate.getDate()===d;

  if (!open) return null;
  return (
    <div style={{ ...dropPanel, left: alignRight?'auto':0, right: alignRight?0:'auto', padding: '1.2rem 1.2rem 1rem', width: '23rem' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
        <button type="button" onClick={() => viewMonth===0 ? (setViewMonth(11),setViewYear(y=>y-1)) : setViewMonth(m=>m-1)}
          style={{ background:'none', border:'none', cursor:'pointer', padding:'0.5rem', display:'flex', borderRadius:'50%' }}><ArrowLeft /></button>
        <span style={{ fontFamily:fonts.serif, fontWeight:300, fontSize:'1.4rem', color:navy }}>{MONTHS[viewMonth]}, {viewYear}</span>
        <button type="button" onClick={() => viewMonth===11 ? (setViewMonth(0),setViewYear(y=>y+1)) : setViewMonth(m=>m+1)}
          style={{ background:'none', border:'none', cursor:'pointer', padding:'0.5rem', display:'flex', borderRadius:'50%' }}><ArrowRight /></button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:'0.3rem' }}>
        {DAYS.map(d => <div key={d} style={{ fontFamily:fonts.sans, fontSize:'1rem', fontWeight:600, color:'rgba(13,47,68,0.4)', textAlign:'center', padding:'0.2rem 0', letterSpacing:'0.04em' }}>{d}</div>)}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'0.12rem' }}>
        {cells.map((d,i) => {
          if (d===null) return <div key={i} />;
          const dis=isDis(d), sel=isSel(d), tod=isTod(d);
          return (
            <button key={i} type="button"
              onClick={() => { if(!dis){ onChange(toISO(new Date(viewYear,viewMonth,d))); setOpen(false); } }}
              style={{
                height:'2.7rem', borderRadius:'50%',
                border: tod&&!sel ? `1px solid rgba(13,47,68,0.3)` : 'none',
                backgroundColor: sel?navy:'transparent',
                color: sel?'#fff':dis?'rgba(13,47,68,0.2)':navy,
                fontFamily:fonts.sans, fontSize:'1.15rem', fontWeight:tod?600:300,
                cursor:dis?'default':'pointer',
                display:'flex', alignItems:'center', justifyContent:'center',
                transition:'background 0.12s ease',
              }}
              onMouseEnter={e => { if(!dis&&!sel) e.currentTarget.style.backgroundColor='rgba(13,47,68,0.08)'; }}
              onMouseLeave={e => { if(!sel) e.currentTarget.style.backgroundColor='transparent'; }}
            >{d}</button>
          );
        })}
      </div>
      <div style={{ marginTop:'0.8rem', textAlign:'right', borderTop:'1px solid rgba(13,47,68,0.07)', paddingTop:'0.7rem' }}>
        <button type="button"
          onClick={() => { if(!minDate||todayDate>=minDate){ onChange(toISO(todayDate)); setOpen(false); } }}
          style={{ background:'none', border:'none', cursor:'pointer', fontFamily:fonts.sans, fontSize:'1.1rem', color:colors.gold }}
        >Today</button>
      </div>
    </div>
  );
}

/* ── Main widget ── */
export default function CheckAvailabilityType2({ rooms: roomTypes = [], showOnMobile = false, showTopBorder = false }: { rooms?: CmsRoom[]; showOnMobile?: boolean; showTopBorder?: boolean }) {
  const router       = useRouter();
  const pathname     = usePathname();
  const searchParams = useSearchParams();
  const onRoomsPage  = pathname === '/rooms';
  const selection    = useRoomSelection(); // null outside RoomSelectionProvider

  const today    = toISO(new Date());
  const tomorrow = toISO(new Date(Date.now() + 86_400_000));

  const [visible,        setVisible]        = useState(false);
  const [isFixed,        setIsFixed]        = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [roomCounts,   setRoomCounts]   = useState<Record<string,number>>({});
  const [guestPerRoom, setGuestPerRoom] = useState<Record<string,GuestEntry>>({});
  const [simpleAdults,   setSimpleAdults]   = useState(1);
  const [simpleChildren, setSimpleChildren] = useState(0);
  // Initialize empty so server and client both render "Select date" during hydration
  const [checkin,  setCheckin]  = useState('');
  const [checkout, setCheckout] = useState('');
  const [openPanel, setOpenPanel] = useState<'rooms'|'guests'|'checkin'|'checkout'|null>(null);
  const [loading,          setLoading]          = useState(false);
  const [navigatingToBook, setNavigatingToBook] = useState(false);
  const [noRooms,          setNoRooms]          = useState(false);
  const [partialMsg,       setPartialMsg]       = useState<string | null>(null);

  // Compact bar height — always start with the SSR fallback so server and client
  // agree during hydration; update to the real measured value after mount.
  const [compactBarH, setCompactBarH] = useState(92.8);

  useEffect(() => {
    const rpx = parseFloat(getComputedStyle(document.documentElement).fontSize);
    setCompactBarH(window.innerWidth >= 768 ? 5.8 * rpx : 3 * rpx);
  }, []);

  /* ── Dynamic header height (handles scroll-shrink animation) ── */
  const navHRef = useRef(NAV_H_FALLBACK);
  const [navHPx, setNavHPx] = useState(NAV_H_FALLBACK);
  const measureNav = useCallback(() => {
    const h = document.querySelector('header')?.offsetHeight;
    if (h && h !== navHRef.current) { navHRef.current = h; setNavHPx(h); }
  }, []);
  useEffect(() => {
    measureNav();
    let t: ReturnType<typeof setTimeout>;
    const onScrollResize = () => { clearTimeout(t); t = setTimeout(measureNav, 60); };
    window.addEventListener('scroll', onScrollResize, { passive: true });
    window.addEventListener('resize', onScrollResize, { passive: true });
    return () => { window.removeEventListener('scroll', onScrollResize); window.removeEventListener('resize', onScrollResize); clearTimeout(t); };
  }, [measureNav]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  /* Single ref covering the entire widget — one click-outside check for both desktop and mobile */
  const widgetRef   = useRef<HTMLDivElement>(null);

  /* Close open panel when clicking outside the whole widget */
  useClickOutside(widgetRef, () => setOpenPanel(null), openPanel !== null);

  /* ── Home page: fixed bar fades in after hero scrolls past ── */
  useEffect(() => {
    if (showOnMobile) return;
    function onScroll() {
      if (!sentinelRef.current) return;
      const top = sentinelRef.current.getBoundingClientRect().top;
      setVisible(top <= navHRef.current);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [showOnMobile]);

  /* ── Collapse mobile pill on scroll when expanded ── */
  useEffect(() => {
    if (!mobileExpanded) return;
    const startY = window.scrollY;
    function onScroll() {
      if (Math.abs(window.scrollY - startY) > 8) setMobileExpanded(false);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [mobileExpanded]);

  /* ── Other pages: bar starts inline, becomes fixed when scrolled past ── */
  useEffect(() => {
    if (!showOnMobile) return;
    function onScroll() {
      if (!sentinelRef.current) return;
      const fixed = sentinelRef.current.getBoundingClientRect().top <= navHRef.current;
      setIsFixed(fixed);
      if (!fixed) setMobileExpanded(false);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [showOnMobile]);

  /* ── Sync state from URL params on first mount; also sets date defaults post-hydration ── */
  useEffect(() => {
    const ci = searchParams.get('checkin');
    const co = searchParams.get('checkout');
    setCheckin(ci || toISO(new Date()));
    setCheckout(co || toISO(new Date(Date.now() + 86_400_000)));

    // r = room-type counts: slug:count,...
    const rParam = searchParams.get('r');
    if (rParam) {
      const counts: Record<string, number> = {};
      rParam.split(',').forEach(part => {
        const [slug, n] = part.split(':');
        if (slug && n && !isNaN(Number(n))) counts[slug] = Number(n);
      });
      if (Object.keys(counts).length) setRoomCounts(counts);
    }

    // g = per-room guests: slug:adults:children,...
    const gParam = searchParams.get('g');
    if (gParam) {
      const guests: Record<string, GuestEntry> = {};
      gParam.split(',').forEach(part => {
        const [slug, a, c] = part.split(':');
        if (slug) guests[slug] = { adults: Math.max(1, parseInt(a) || 1), children: Math.max(0, parseInt(c) || 0) };
      });
      if (Object.keys(guests).length) setGuestPerRoom(guests);
    }

    // Fallback simple guests when no room types were passed
    const adultsParam = searchParams.get('adults');
    const childrenParam = searchParams.get('children');
    if (adultsParam)   setSimpleAdults(Math.max(1, parseInt(adultsParam) || 1));
    if (childrenParam) setSimpleChildren(Math.max(0, parseInt(childrenParam) || 0));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Measure actual compact bar height when widget first becomes fixed ── */
  useEffect(() => {
    if (!isFixed || !widgetRef.current) return;
    // mobileExpanded is always false at this point (reset by onScroll above)
    const h = widgetRef.current.offsetHeight;
    if (h > 0) setCompactBarH(h);
  }, [isFixed]);

  const hasTypes      = roomTypes.length > 0;
  const selectedRooms = roomTypes.filter(rt => (roomCounts[rt.slug]??0) > 0);

  const { maxA: maxAdultsPerRoom, maxC: maxChildrenPerRoom } = maxCapacity(roomTypes);

  function handleSimpleAdults(v: number) {
    setSimpleAdults(v);
    const needed = neededRooms(v, simpleChildren, maxAdultsPerRoom, maxChildrenPerRoom);
    if (needed > (roomCounts['__rooms'] ?? 1)) setRoomCounts(prev => ({ ...prev, '__rooms': needed }));
  }
  function handleSimpleChildren(v: number) {
    setSimpleChildren(v);
    const needed = neededRooms(simpleAdults, v, maxAdultsPerRoom, maxChildrenPerRoom);
    if (needed > (roomCounts['__rooms'] ?? 1)) setRoomCounts(prev => ({ ...prev, '__rooms': needed }));
  }
  function handleSimpleRoomCount(n: number) {
    setRoomCounts(prev => ({ ...prev, '__rooms': n }));
    const capA = n * maxAdultsPerRoom;
    const capC = n * maxChildrenPerRoom;
    if (simpleAdults   > capA) setSimpleAdults(capA);
    if (simpleChildren > capC) setSimpleChildren(capC);
  }

  const minCheckout = checkin
    ? toISO(new Date(new Date(checkin+'T00:00:00').getTime()+86_400_000))
    : tomorrow;

  function handleCheckinChange(val: string) {
    setCheckin(val);
    setNoRooms(false);
    setPartialMsg(null);
    selection?.clear();
    if (checkout <= val) setCheckout(toISO(new Date(new Date(val+'T00:00:00').getTime()+86_400_000)));
  }

  function handleCheckoutChange(val: string) {
    setCheckout(val);
    setNoRooms(false);
    setPartialMsg(null);
    selection?.clear();
  }

  const roomSummary = (() => {
    if (hasTypes) {
      const sel   = roomTypes.filter(rt=>(roomCounts[rt.slug]??0)>0);
      const total = sel.reduce((s,rt)=>s+(roomCounts[rt.slug]??0),0);
      if (sel.length===0) return '1 Room';
      if (sel.length===1) return `${roomCounts[sel[0].slug]} ${sel[0].name}`;
      return `${total} Rooms`;
    }
    const n = roomCounts['__rooms']??1;
    return `${n} Room${n!==1?'s':''}`;
  })();

  const guestSummary = (() => {
    let a: number, c: number;
    if (hasTypes && selectedRooms.length>0) {
      a = selectedRooms.reduce((s,rt)=>s+(guestPerRoom[rt.slug]?.adults??1),0);
      c = selectedRooms.reduce((s,rt)=>s+(guestPerRoom[rt.slug]?.children??0),0);
    } else { a=simpleAdults; c=simpleChildren; }
    return c>0 ? `${a} Adult${a!==1?'s':''} · ${c} Child${c!==1?'ren':''}` : `${a} Adult${a!==1?'s':''}`;
  })();

  function toggle(p: typeof openPanel) { setOpenPanel(prev=>prev===p?null:p); }

  function scrollToRoomsList() {
    const el = document.getElementById('rooms-list');
    if (!el) return;

    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const stickyLayer = document.querySelector('header')?.children[1] as HTMLElement | undefined;
    const navH = stickyLayer?.getBoundingClientRect().bottom ?? 7.6 * rootPx;

    const sentinel = document.querySelector('[data-avail-sentinel]') as HTMLElement | null;
    const compactH = sentinel ? parseFloat(sentinel.getAttribute('data-compact-h') ?? '0') : 0;

    const widget = document.querySelector('[data-avail-widget]') as HTMLElement | null;
    const widgetFixed = widget ? getComputedStyle(widget).position === 'fixed' : false;
    let roomsAbsTop = el.getBoundingClientRect().top + window.scrollY;
    if (!widgetFixed && widget) {
      const delta = Math.max(0, widget.offsetHeight - compactH);
      roomsAbsTop -= delta;
    }

    window.scrollTo({ top: roomsAbsTop - navH - compactH - 16, behavior: 'smooth' });
  }

  function buildRoomsUrl(): string {
    const p = new URLSearchParams({ checkin, checkout });
    if (hasTypes && selectedRooms.length > 0) {
      p.set('r', selectedRooms.map(rt => `${rt.slug}:${roomCounts[rt.slug] ?? 1}`).join(','));
      p.set('g', selectedRooms.map(rt =>
        `${rt.slug}:${guestPerRoom[rt.slug]?.adults ?? 1}:${guestPerRoom[rt.slug]?.children ?? 0}`
      ).join(','));
    } else {
      if (simpleAdults > 1)   p.set('adults',   String(simpleAdults));
      if (simpleChildren > 0) p.set('children', String(simpleChildren));
    }
    return `/rooms?${p.toString()}`;
  }

  async function handleBookNow() {
    router.push('?contact-booking=true', { scroll: false });
    /*
    if (!selection?.state.active || selection.state.selected.length === 0) return;
    setLoading(true);
    try {
      const { selected, checkin: ci, checkout: co, adults: a, children: c } = selection.state;
      const items: Record<string, number> = Object.fromEntries(selected.map(s => [s, 1]));
      const url = await buildSignedBookingUrl({
        items,
        checkin:  ci,
        checkout: co,
        rooms:    selected.length,
        adults:   a,
        children: c > 0 ? c : undefined,
      });
      setNavigatingToBook(true);
      router.push(url);
      // navigatingToBook stays true — overlay persists until the booking page loads
    } catch {
      // sign failed — let user try again
    } finally {
      setLoading(false);
    }
    */
  }

  async function handleSubmit() {
    setLoading(true);
    setNoRooms(false);
    setPartialMsg(null);
    try {
      const fetchResult = await fetchAvailableRoomTypes(checkin, checkout);
      const bypassAvailability = process.env.isByPassAvailablity === 'true' || process.env.NEXT_PUBLIC_IS_BYPASS_AVAILABLITY === 'true';
      const result = bypassAvailability
        ? { available: roomTypes.map(rt => Number(rt.external_room_type_id)).filter(id => !isNaN(id)) }
        : fetchResult;

      if (!result?.available?.length) {
        setNoRooms(true);
        return;
      }

      // When specific room types are selected in the widget
      if (hasTypes && selectedRooms.length > 0) {
        // result.available contains Sandy roomTypeIds — must match via external_room_type_id
        const availableSet  = new Set(result.available);
        const unavailable   = selectedRooms.filter(rt => !rt.external_room_type_id || !availableSet.has(rt.external_room_type_id));
        const availSelected = selectedRooms.filter(rt => rt.external_room_type_id != null && availableSet.has(rt.external_room_type_id));
        const totalAdults   = selectedRooms.reduce((s, rt) => s + (guestPerRoom[rt.slug]?.adults   ?? 1), 0);
        const totalChildren = selectedRooms.reduce((s, rt) => s + (guestPerRoom[rt.slug]?.children ?? 0), 0);

        // On rooms page: always enter selection mode — pre-tick available rooms,
        // show sold-out overlay on unavailable ones so user can swap
        if (onRoomsPage && selection) {
          if (unavailable.length > 0) {
            setPartialMsg(
              unavailable.length === selectedRooms.length
                ? 'None of your selected rooms are available for these dates. Please choose from below.'
                : `${unavailable.map(rt => rt.name).join(', ')} ${unavailable.length === 1 ? 'is' : 'are'} fully booked — please select an alternative below.`
            );
          }
          selection.enter({
            target:      0,
            availableIds: result.available,
            preSelected:  availSelected.map(rt => rt.slug),
            checkin,
            checkout,
            adults:   totalAdults,
            children: totalChildren,
          });
          setOpenPanel(null);
          setTimeout(scrollToRoomsList, 150);
          return;
        }

        // Off rooms page (e.g. home page widget): show error or go straight to /book
        if (unavailable.length > 0) {
          setPartialMsg(
            unavailable.map(rt => rt.name).join(', ') +
            (unavailable.length === 1 ? ' is' : ' are') +
            ' fully booked for these dates.'
          );
          return;
        }

        const items: Record<string, number> = {};
        for (const rt of selectedRooms) items[rt.slug] = roomCounts[rt.slug] ?? 1;
        const totalRooms = selectedRooms.reduce((s, rt) => s + (roomCounts[rt.slug] ?? 1), 0);
        try {
          router.push('?contact-booking=true', { scroll: false });
          return;
          /*
          const url = await buildSignedBookingUrl({
            items, checkin, checkout,
            rooms:    totalRooms,
            adults:   totalAdults,
            children: totalChildren > 0 ? totalChildren : undefined,
          });
          router.push(url);
          return;
          */
        } catch {
          // fall through on sign error
        }
      }

      // Generic path: on rooms page with selection provider → enter selection mode
      if (onRoomsPage && selection) {
        const target = !hasTypes ? (roomCounts['__rooms'] ?? 1) : 0;
        selection.enter({
          target,
          availableIds: result.available,
          checkin,
          checkout,
          adults:   simpleAdults,
          children: simpleChildren,
        });
        setOpenPanel(null);
        setTimeout(scrollToRoomsList, 150);
        return;
      }

      router.push(buildRoomsUrl(), { scroll: false });
      if (onRoomsPage) setTimeout(scrollToRoomsList, 150);
    } catch {
      router.push(buildRoomsUrl(), { scroll: false });
      if (onRoomsPage) setTimeout(scrollToRoomsList, 150);
    } finally {
      setLoading(false);
    }
  }

  /* Shared dropdown props */
  const roomsDrop  = {
    roomCounts,
    onCount: (s: string, n: number) => {
      if (!hasTypes && s === '__rooms') { handleSimpleRoomCount(n); }
      else { setRoomCounts(prev => ({ ...prev, [s]: n })); }
    },
    roomTypes,
  };
  const guestsDrop = {
    selectedRooms, guestPerRoom,
    onGuestChange: (slug:string,f:keyof GuestEntry,v:number)=>setGuestPerRoom(prev=>({...prev,[slug]:{...(prev[slug]??{adults:1,children:0}),[f]:v}})),
    simpleAdults, simpleChildren,
    onSimpleAdults: handleSimpleAdults, onSimpleChildren: handleSimpleChildren,
    hasTypes,
    simpleRoomCount: roomCounts['__rooms'] ?? 1,
    simpleMaxAdultsPerRoom: maxAdultsPerRoom,
    simpleMaxChildrenPerRoom: maxChildrenPerRoom,
  };

  /* ─── Bar styles ─── */
  const barStyle: React.CSSProperties = {
    position: 'fixed',
    top: `${navHPx}px`,
    left: 0,
    right: 0,
    zIndex: 40,
    background: 'rgba(237,236,234,0.97)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(120, 105, 71,0.35)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.09)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(-1rem)',
    transition: 'opacity 0.35s ease, transform 0.35s ease',
    pointerEvents: visible ? 'auto' : 'none',
  };

  const fieldWrap: React.CSSProperties = { position: 'relative', flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'stretch' };

  return (
    <>
      {/* Navigating overlay — shown while router.push is loading the booking page */}
      {navigatingToBook && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(13, 47, 68, 0.96)',
          backdropFilter: 'blur(10px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: '2rem', padding: '2rem',
        }}>
          <style dangerouslySetInnerHTML={{ __html: '@keyframes spin { to { transform: rotate(360deg); } }' }} />
          <Loader2 size={44} color="rgba(212,175,95,0.9)" strokeWidth={1.4}
            style={{ animation: 'spin 1.1s linear infinite', flexShrink: 0 }} />
          <div style={{ textAlign: 'center', maxWidth: '36rem' }}>
            <p style={{ fontFamily: fonts.serif, fontSize: 'clamp(2rem,4vw,2.6rem)', color: '#fff', fontWeight: 300, letterSpacing: '0.06em', marginBottom: '0.8rem' }}>
              Navigating to booking page
            </p>
            <p style={{ fontFamily: fonts.sans, fontSize: '1.3rem', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Please wait…
            </p>
          </div>
        </div>
      )}

      {/* Sentinel / spacer — height matches actual compact bar on every breakpoint */}
      <div
        ref={sentinelRef}
        data-avail-sentinel="true"
        data-compact-h={compactBarH}
        style={{ height: showOnMobile && isFixed ? `${compactBarH}px` : 0, pointerEvents: 'none' }}
        aria-hidden="true"
      />

      {/* widgetRef covers the entire widget — one click-outside listener handles both desktop and mobile */}
      <div
        ref={widgetRef}
        data-avail-widget="true"
        className={showOnMobile ? undefined : 'hidden md:block'}
        style={showOnMobile ? {
          position: isFixed ? 'fixed' : 'relative',
          top: isFixed ? `${navHPx}px` : 'auto',
          left: 0, right: 0, zIndex: 40,
          background: 'rgba(237,236,234,0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: showTopBorder && !isFixed ? '1px solid rgba(120, 105, 71,0.34)' : undefined,
          borderBottom: '1px solid rgba(120, 105, 71,0.35)',
          boxShadow: isFixed ? '0 6px 24px rgba(0,0,0,0.09)' : '0 2px 12px rgba(0,0,0,0.06)',
        } : barStyle}
      >

        {/* ── Desktop ── */}
        <div className="hidden md:flex" style={{ maxWidth: '144rem', margin: '0 auto', height: '5.8rem', alignItems: 'stretch' }}>

          <VDivider />

          {/* Rooms */}
          <div style={fieldWrap}>
            <BarField label="Rooms" icon={<BedIcon />} value={roomSummary} open={openPanel==='rooms'} onClick={()=>toggle('rooms')} />
            <RoomsDropdown {...roomsDrop} open={openPanel==='rooms'} setOpen={v=>setOpenPanel(v?'rooms':null)} />
          </div>

          <VDivider />

          {/* Guests */}
          <div style={fieldWrap}>
            <BarField label="Guests" icon={<PeopleIcon />} value={guestSummary} open={openPanel==='guests'} onClick={()=>toggle('guests')} />
            <GuestsDropdown {...guestsDrop} open={openPanel==='guests'} setOpen={v=>setOpenPanel(v?'guests':null)} />
          </div>

          <VDivider />

          {/* Check-in */}
          <div style={fieldWrap}>
            <BarField label="Check-in" icon={<CalendarIcon />} value={fmtShort(checkin)} open={openPanel==='checkin'} onClick={()=>toggle('checkin')} />
            <DateDropdown value={checkin} onChange={handleCheckinChange} min={today} open={openPanel==='checkin'} setOpen={v=>setOpenPanel(v?'checkin':null)} />
          </div>

          <VDivider />

          {/* Check-out */}
          <div style={fieldWrap}>
            <BarField label="Check-out" icon={<CalendarIcon />} value={fmtShort(checkout)} open={openPanel==='checkout'} onClick={()=>toggle('checkout')} />
            <DateDropdown value={checkout} onChange={handleCheckoutChange} min={minCheckout} open={openPanel==='checkout'} setOpen={v=>setOpenPanel(v?'checkout':null)} alignRight />
          </div>

          <VDivider />

          {/* Submit / Book Now — switches when selection mode is active */}
          {selection?.state.active ? (
            <div style={{ display: 'flex', alignItems: 'stretch', flexShrink: 0 }}>
              {/* Clear button */}
              <button
                type="button"
                onClick={() => selection.clear()}
                style={{
                  padding: '0 1.6rem',
                  background: 'transparent', border: 'none',
                  fontFamily: fonts.sans, fontSize: '1.15rem', fontWeight: 400,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: 'rgba(13,47,68,0.55)', cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                ✕ Clear
              </button>
              <VDivider />
              {/* Book Now */}
              {(() => {
                const n      = selection.state.selected.length;
                const target = selection.state.target;
                const ready  = n > 0 && (target === 0 || n === target);
                const label  = n === 0
                  ? (target > 0 ? `Select ${target} Room${target !== 1 ? 's' : ''}` : 'Select Rooms')
                  : `Book Now · ${n} Room${n !== 1 ? 's' : ''}`;
                return (
                  <button onClick={handleBookNow} disabled={loading || !ready} className="btn-gold btn-luxury-shine" style={{
                    flexShrink: 0, padding: '0 clamp(2.4rem,3vw,4rem)',
                    position: 'relative',
                    opacity: loading ? 0.75 : ready ? 1 : 0.5,
                    cursor: loading ? 'wait' : ready ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius:'0'
                  }}>
                    <span style={{ visibility: 'hidden', pointerEvents: 'none' }}>Book Now · 0 Rooms</span>
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
                      {loading ? <><ButtonSpinner color="#fff" size={16} /> Navigating…</> : label}
                    </span>
                  </button>
                );
              })()}
            </div>
          ) : (
            <button onClick={handleSubmit} disabled={loading} className="btn-luxury-shine" style={{
              flexShrink: 0, padding: '0 clamp(2.4rem,3vw,4rem)',
              position: 'relative',
              backgroundColor: colors.navyDeep, color: '#fff', border: 'none',
              fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
              letterSpacing: '0.15em', textTransform: 'uppercase',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.75 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ visibility: 'hidden', pointerEvents: 'none' }}>Check Availability</span>
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
                {loading ? <><ButtonSpinner color="#fff" size={16} /> Checking…</> : 'Check Availability'}
              </span>
            </button>
          )}
        </div>

        {/* Desktop partial-availability / no-rooms banner */}
        {(noRooms || partialMsg) && (
          <div className="hidden md:block" style={{
            padding: '0.7rem clamp(2.4rem,3vw,4rem)',
            background: 'rgba(180,83,9,0.07)',
            borderTop: '1px solid rgba(180,83,9,0.18)',
            fontFamily: fonts.sans, fontSize: '1.15rem', color: '#b45309',
            textAlign: 'center', lineHeight: 1.5,
          }}>
            {partialMsg ?? 'No rooms available for those dates.'}
          </div>
        )}

        {/* ── Mobile ── */}
        <div className="flex flex-col md:hidden">

          {/* Sticky pill: booking summary + chevron */}
          {isFixed && (
            <button type="button" onClick={() => setMobileExpanded(v => !v)} style={{
              width: '100%', padding: '0.75rem 1.2rem',
              backgroundColor: colors.navyDeep, color: '#fff',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '0.8rem',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: fonts.sans, fontSize: '0.88rem', fontWeight: 400, letterSpacing: '0.04em', opacity: 0.92, overflow: 'hidden' }}>
                <CalendarIcon />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {fmtShort(checkin)} – {fmtShort(checkout)}
                </span>
                <span style={{ opacity: 0.6, flexShrink: 0 }}>·</span>
                <span style={{ flexShrink: 0 }}>{guestSummary}</span>
              </span>
              <ChevronDown open={mobileExpanded} color="rgba(255,255,255,0.85)" />
            </button>
          )}

          {/* Full form: always visible when inline; visible when pill is expanded */}
          {(!isFixed || mobileExpanded) && (
            <div style={{ padding: '0.7rem 1rem' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginBottom:'0.5rem' }}>
                {([
                  { key:'rooms',    label:'Rooms',     icon:<BedIcon />,      val:roomSummary,        drop:<RoomsDropdown {...roomsDrop} open={openPanel==='rooms'} setOpen={v=>setOpenPanel(v?'rooms':null)} /> },
                  { key:'guests',   label:'Guests',    icon:<PeopleIcon />,   val:guestSummary,       drop:<GuestsDropdown {...guestsDrop} open={openPanel==='guests'} setOpen={v=>setOpenPanel(v?'guests':null)} alignRight /> },
                  { key:'checkin',  label:'Check-in',  icon:<CalendarIcon />, val:fmtShort(checkin),  drop:<DateDropdown value={checkin} onChange={handleCheckinChange} min={today} open={openPanel==='checkin'} setOpen={v=>setOpenPanel(v?'checkin':null)} /> },
                  { key:'checkout', label:'Check-out', icon:<CalendarIcon />, val:fmtShort(checkout), drop:<DateDropdown value={checkout} onChange={handleCheckoutChange} min={minCheckout} open={openPanel==='checkout'} setOpen={v=>setOpenPanel(v?'checkout':null)} alignRight /> },
                ] as const).map(({key, label, icon, val, drop}) => (
                  <div key={key} style={{ position:'relative' }}>
                    <button type="button" onClick={()=>toggle(key as typeof openPanel)} style={{
                      width:'100%', border:'1px solid rgba(13,47,68,0.14)', borderRadius:'0.5rem',
                      background:'rgba(255,255,255,0.6)', padding:'0.6rem 0.9rem',
                      display:'flex', flexDirection:'column', alignItems:'flex-start', cursor:'pointer',
                    }}>
                      <span style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontFamily:fonts.sans, fontSize:'0.75rem', fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(13,47,68,0.5)', marginBottom:'0.15rem' }}>{icon} {label}</span>
                      <span style={{ fontFamily:fonts.sans, fontSize:'1.1rem', color:navy, display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>{val} <ChevronDown open={openPanel===key} /></span>
                    </button>
                    {drop}
                  </div>
                ))}
              </div>
              {(noRooms || partialMsg) && (
                <p style={{
                  fontFamily: fonts.sans, fontSize: '1.1rem', color: '#b45309',
                  background: 'rgba(180,83,9,0.08)', border: '1px solid rgba(180,83,9,0.2)',
                  borderRadius: '0.4rem', padding: '0.6rem 0.8rem', marginBottom: '0.5rem',
                  textAlign: 'center', lineHeight: 1.5,
                }}>
                  {partialMsg ?? 'No rooms available for those dates.'}
                </p>
              )}
              {selection?.state.active ? (() => {
                const n      = selection.state.selected.length;
                const target = selection.state.target;
                const ready  = n > 0 && (target === 0 || n === target);
                const label  = n === 0
                  ? (target > 0 ? `Select ${target} Room${target !== 1 ? 's' : ''}` : 'Select Rooms')
                  : `Book Now · ${n} Room${n !== 1 ? 's' : ''}`;
                return (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => selection.clear()}
                      style={{
                        flexShrink: 0,
                        height: '4.4rem', padding: '0 1.2rem',
                        border: '1px solid rgba(13,47,68,0.2)', borderRadius: '0',
                        background: 'transparent',
                        fontFamily: fonts.sans, fontSize: '1.1rem', fontWeight: 500,
                        color: 'rgba(13,47,68,0.6)', cursor: 'pointer',
                      }}
                    >
                      ✕ Clear
                    </button>
                    <button
                      onClick={handleBookNow}
                      disabled={loading || !ready}
                      className="btn-gold btn-luxury-shine"
                      style={{
                        flex: 1,
                        opacity: loading ? 0.75 : ready ? 1 : 0.5,
                        cursor: loading ? 'wait' : ready ? 'pointer' : 'default',
                        gap: '0.6rem',
                        borderRadius: '0'
                      }}
                    >
                      {loading ? <><ButtonSpinner color="#fff" size={16} /> Navigating…</> : label}
                    </button>
                  </div>
                );
              })() : (
                <button onClick={handleSubmit} disabled={loading} className="btn-primary btn-luxury-shine"
                  style={{
                    width:'100%',
                    cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.75 : 1, gap:'0.6rem',
                  }}>{loading ? <><ButtonSpinner color="#fff" size={16} /> Checking…</> : 'Check Availability'}</button>
              )}
            </div>
          )}
        </div>

      </div>
    </>
  );
}
